import { useEffect } from 'react'

import { useGetVersionQuery } from 'gqlTypes'
import { getVersionRenderStatus } from 'utils/renderStatus'

const POLLING_INTERVAL = 15000
type RefetchFunction = ReturnType<typeof useGetVersionQuery>['refetch']

/**
 * Call the document version refetch function every X seconds until
 * the document version render status is "ready" (or error)
 */
function usePollDocumentVersions(
  refetch: RefetchFunction,
  parsedRenderStatus: string
) {
  // Handle polling
  useEffect(() => {
    const isWaitingForDocumentToBeProcessed =
      parsedRenderStatus === 'uploading' || parsedRenderStatus === 'processing'
    if (!isWaitingForDocumentToBeProcessed) {
      // Document is ready we are not waiting for anything
      return
    }

    // Refetch the version every X seconds to update the render status.
    const intervalId = window.setInterval(() => {
      const promise = refetch()
      if (promise?.catch) {
        promise.catch(() => {
          /**
           * Refetch errors are not automatically handled and cannot be handled
           * by a queries onError handler.
           *
           * This explicitly adds a catch block to the refetch promise chain and
           * ignores the error.
           *
           * Should help reduce https://github.com/sketch-hq/Cloud/issues/998.
           * Especially since this happens every 15 seconds.
           **/
        })
      }
    }, POLLING_INTERVAL)

    return () => {
      clearInterval(intervalId)
    }
  }, [refetch, parsedRenderStatus])
}

/**
 * Wrapper around useGetVersionQuery to refetch document version
 * every X seconds in case the document is still processing.
 * We need to do this refetch because as a way to be notified when the document
 * version render status changes ("uploading", "processing", "ready").
 * Once the document is "ready" we can stop polling.
 */
export function useGetVersionQueryWithPolling(
  shareID: string,
  versionShortId: string
) {
  const queryResult = useGetVersionQuery({
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.versionShortId !== curr?.versionShortId,
    variables: {
      shareIdentifier: shareID,
      versionShortId,
    },
  })

  const { data, refetch } = queryResult
  const renderStatus = getVersionRenderStatus(data?.share?.version)

  usePollDocumentVersions(refetch, renderStatus)

  return queryResult
}
